/**
 * SMTP extension Settings card - named connections + Send test.
 * Passwords go to workspace_secrets (SMTP_PASS_<SLUG>); never into extension config.
 */
import { useCallback, useEffect, useState } from 'react'
import { Eye, EyeOff, Loader2, Mail, Plus, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { fetchWithAuth } from '@/lib/api'
import { toast } from 'sonner'
import { useAuth } from '@/providers/auth-provider'
import { usePersistedState } from '@/hooks/use-persisted-state'
import { StorageKeys } from '@/lib/storage/keys'
import { StorageSchemas } from '@/lib/storage/schemas'
import { testIds } from '@/lib/test-ids'
import { parseTestRecipients, rememberRecipients } from './smtp-test-recipients'

type SmtpCrypto = 'tls' | 'ssl' | 'none'

interface SmtpConnection {
  name: string
  from_email: string
  from_name: string
  smtp_host: string
  smtp_port: number
  smtp_user: string
  smtp_crypto: SmtpCrypto
  secret_name?: string
  has_password?: boolean
}

interface SmtpStatus {
  enabled: boolean
  daily_cap: number
  connections: SmtpConnection[]
}

const emptyForm = (): SmtpConnection & { smtp_pass: string } => ({
  name: '',
  from_email: '',
  from_name: '',
  smtp_host: '',
  smtp_port: 587,
  smtp_user: '',
  smtp_crypto: 'tls',
  smtp_pass: '',
})

function orgQs(organizationId?: string | null): string {
  if (!organizationId) return ''
  return `?organizationId=${encodeURIComponent(organizationId)}`
}

interface SmtpIntegrationSectionProps {
  organizationId?: string | null
}

export function SmtpIntegrationSection({ organizationId }: SmtpIntegrationSectionProps) {
  const { user } = useAuth()
  const [status, setStatus] = useState<SmtpStatus | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [testingName, setTestingName] = useState<string | null>(null)
  const [showPass, setShowPass] = useState(false)
  const [editing, setEditing] = useState(false)
  const [form, setForm] = useState(emptyForm)
  const [testError, setTestError] = useState<string | null>(null)
  const [savedRecipients, setSavedRecipients] = usePersistedState({
    key: StorageKeys.SMTP_TEST_RECIPIENTS,
    defaultValue: { lastTo: '', recent: [] as string[] },
    schema: StorageSchemas.smtpTestRecipients,
  })
  const [testTo, setTestTo] = useState('')

  useEffect(() => {
    if (testTo) return
    if (savedRecipients.lastTo) {
      setTestTo(savedRecipients.lastTo)
      return
    }
    if (user?.email) setTestTo(user.email)
  }, [savedRecipients.lastTo, testTo, user?.email])

  const load = useCallback(async () => {
    if (!organizationId) return
    setLoading(true)
    try {
      const res = await fetchWithAuth(`/api/smtp/status${orgQs(organizationId)}`)
      if (!res.ok) throw new Error('Failed to load SMTP status')
      const data = (await res.json()) as SmtpStatus
      setStatus(data)
    } catch (e) {
      console.error(e)
      toast.error('Failed to load SMTP settings')
    } finally {
      setLoading(false)
    }
  }, [organizationId])

  useEffect(() => {
    void load()
  }, [load])

  const startEdit = (conn?: SmtpConnection) => {
    if (conn) {
      setForm({
        ...emptyForm(),
        ...conn,
        smtp_port: conn.smtp_port || 587,
        smtp_pass: '',
      })
    } else {
      setForm(emptyForm())
    }
    setEditing(true)
  }

  const handleSave = async () => {
    if (!organizationId) return
    if (!form.name.trim() || !form.from_email.trim() || !form.smtp_host.trim() || !form.smtp_user.trim()) {
      toast.error('Connection id, from email, host, and user are required')
      return
    }
    setSaving(true)
    try {
      const body: Record<string, unknown> = {
        organizationId,
        connection: {
          name: form.name.trim(),
          from_email: form.from_email.trim(),
          from_name: form.from_name.trim(),
          smtp_host: form.smtp_host.trim(),
          smtp_port: Number(form.smtp_port) || 587,
          smtp_user: form.smtp_user.trim(),
          smtp_crypto: form.smtp_crypto,
        },
      }
      if (form.smtp_pass) body.smtp_pass = form.smtp_pass
      const res = await fetchWithAuth(`/api/smtp/connection${orgQs(organizationId)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        throw new Error((err as { error?: string }).error || 'Save failed')
      }
      toast.success('SMTP connection saved')
      setEditing(false)
      await load()
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (name: string) => {
    if (!organizationId) return
    if (!confirm(`Remove SMTP connection "${name}"?`)) return
    try {
      const res = await fetchWithAuth(
        `/api/smtp/connection/${encodeURIComponent(name)}${orgQs(organizationId)}`,
        { method: 'DELETE' },
      )
      if (!res.ok) throw new Error('Delete failed')
      toast.success('Connection removed')
      await load()
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Delete failed')
    }
  }

  const handleSendTest = async (connectionName: string) => {
    if (!organizationId || !connectionName.trim()) {
      toast.error('Save a connection first')
      return
    }
    const to = testTo.trim()
    if (!to) {
      toast.error('Add at least one test recipient')
      return
    }
    setTestingName(connectionName)
    setTestError(null)
    try {
      const res = await fetchWithAuth(`/api/smtp/send-test${orgQs(organizationId)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          organizationId,
          connection: connectionName.trim(),
          to,
        }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error((data as { error?: string }).error || 'Send test failed')
      const sent = parseTestRecipients(to)
      setSavedRecipients((prev) => rememberRecipients(to, prev.recent, sent))
      toast.success(sent.length > 1 ? `Test email sent to ${sent.length} addresses` : 'Test email sent')
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Send test failed'
      setTestError(message)
      toast.error(message)
    } finally {
      setTestingName(null)
    }
  }

  if (!organizationId) {
    return <p className="text-sm text-muted-foreground">Select a workspace first.</p>
  }

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground py-4">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading SMTP…
      </div>
    )
  }

  const connections = status?.connections ?? []

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Tenant-owned outbound mail for flows. Passwords are stored in workspace secrets
        (never in flow events). Platform Resend is not used here.
      </p>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-medium">Connections</h4>
          <Button type="button" size="sm" variant="outline" className="h-8 gap-1" onClick={() => startEdit()}>
            <Plus className="h-3.5 w-3.5" />
            Add
          </Button>
        </div>
        {connections.length === 0 && !editing ? (
          <p className="text-xs text-muted-foreground">No connections yet.</p>
        ) : (
          <ul className="space-y-2">
            {connections.map((c) => (
              <li
                key={c.name}
                className="flex items-center justify-between gap-2 rounded-md border border-border px-3 py-2 text-sm"
              >
                <div className="min-w-0">
                  <div className="font-medium truncate">{c.name}</div>
                  <div className="text-xs text-muted-foreground truncate">
                    {c.from_email} via {c.smtp_host}:{c.smtp_port}
                    {c.has_password ? '' : ' · missing password'}
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button type="button" size="sm" variant="ghost" className="h-7" onClick={() => startEdit(c)}>
                    Edit
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    className="h-7"
                    disabled={Boolean(testingName) || !c.has_password}
                    data-testid={testIds.workspaceSettings.smtpSendTest(c.name)}
                    onClick={() => void handleSendTest(c.name)}
                  >
                    {testingName === c.name ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Send test'}
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    className="h-7 w-7 p-0 text-destructive"
                    onClick={() => void handleDelete(c.name)}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {connections.length > 0 && (
        <div className="space-y-2 rounded-md border border-border p-3">
          <Label htmlFor="smtp-send-test-to" className="text-xs">
            Send test to
          </Label>
          <Input
            id="smtp-send-test-to"
            list="smtp-test-recent"
            value={testTo}
            onChange={(e) => setTestTo(e.target.value)}
            placeholder="you@company.com, other@company.com"
            className="h-8"
            data-testid={testIds.workspaceSettings.smtpSendTestTo}
          />
          <datalist id="smtp-test-recent">
            {savedRecipients.recent.map((email) => (
              <option key={email} value={email} />
            ))}
          </datalist>
          <p className="text-xs text-muted-foreground">
            Comma-separated. Prefills your account email; last used is remembered on this browser.
          </p>
          {testError ? (
            <Alert variant="destructive" data-testid={testIds.workspaceSettings.smtpSendTestError}>
              <AlertDescription>{testError}</AlertDescription>
            </Alert>
          ) : null}
        </div>
      )}

      {editing && (
        <div className="space-y-3 rounded-md border border-border p-4">
          <div className="flex items-center gap-2 text-sm font-medium">
            <Mail className="h-4 w-4 shrink-0" />
            {form.name ? `Edit ${form.name}` : 'New connection'}
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5 sm:col-span-2">
              <Label className="text-xs">Connection id</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="e.g. primary"
                className="h-8"
              />
              <p className="text-xs text-muted-foreground">
                Shown in the list above. Flows reference it as{' '}
                <code className="text-[11px]">connection: …</code>
              </p>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">From email</Label>
              <Input
                value={form.from_email}
                onChange={(e) => setForm((f) => ({ ...f, from_email: e.target.value }))}
                className="h-8"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">From name</Label>
              <Input
                value={form.from_name}
                onChange={(e) => setForm((f) => ({ ...f, from_name: e.target.value }))}
                className="h-8"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Host</Label>
              <Input
                value={form.smtp_host}
                onChange={(e) => setForm((f) => ({ ...f, smtp_host: e.target.value }))}
                placeholder="smtp-relay.brevo.com"
                className="h-8"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Port</Label>
              <Input
                type="number"
                value={form.smtp_port}
                onChange={(e) => setForm((f) => ({ ...f, smtp_port: Number(e.target.value) || 587 }))}
                className="h-8"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Encryption</Label>
              <Select
                value={form.smtp_crypto}
                onValueChange={(value) =>
                  setForm((f) => ({ ...f, smtp_crypto: value as SmtpCrypto }))
                }
              >
                <SelectTrigger className="h-8 shadow-none">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="tls">TLS</SelectItem>
                  <SelectItem value="ssl">SSL</SelectItem>
                  <SelectItem value="none">None</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">User</Label>
              <Input
                value={form.smtp_user}
                onChange={(e) => setForm((f) => ({ ...f, smtp_user: e.target.value }))}
                className="h-8"
              />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label className="text-xs">Password / API key</Label>
              <div className="relative">
                <Input
                  type={showPass ? 'text' : 'password'}
                  value={form.smtp_pass}
                  onChange={(e) => setForm((f) => ({ ...f, smtp_pass: e.target.value }))}
                  placeholder={form.has_password ? 'Leave blank to keep saved password' : undefined}
                  className="h-8 pr-10"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-0.5 top-1/2 h-7 w-7 -translate-y-1/2"
                  onClick={() => setShowPass((v) => !v)}
                  aria-label={showPass ? 'Hide password' : 'Show password'}
                >
                  {showPass ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                </Button>
              </div>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3 pt-2">
            <Button type="button" size="sm" onClick={() => void handleSave()} disabled={saving}>
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Save'}
            </Button>
            <Button type="button" size="sm" variant="ghost" onClick={() => setEditing(false)}>
              Cancel
            </Button>
            <p className="text-xs text-muted-foreground">
              Save first, then Send test from the connection list.
            </p>
          </div>
        </div>
      )}
    </div>
  )
}
